// src/pages/TransfersPage.js
import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Typography, Paper, Grid, Select, MenuItem,
    InputLabel, FormControl, Autocomplete, TextField, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AuthContext from '../context/AuthContext.js';
import api from '../api/index.js';
import TransferDetailsModal from './TransferDetailsModal.js';

const TransfersPage = () => {
    // --- ESTADOS ---
    const { user } = useContext(AuthContext);
    const userStoreId = user?.stores?.[0];

    const [transfers, setTransfers] = useState([]);
    const [allStores, setAllStores] = useState([]); // Lista completa de todas las tiendas
    const [destinationStores, setDestinationStores] = useState([]); // Lista filtrada para el <Select>
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [newTransfer, setNewTransfer] = useState({ destination_store_id: '' });
    const [transferItems, setTransferItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ product: null, quantity: 1 });
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedTransferId, setSelectedTransferId] = useState(null);
    // --- CARGA DE DATOS ---
    useEffect(() => {
        const fetchData = async () => {
            if (!userStoreId) {
                setError('Usuario no asignado a ninguna tienda.');
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const [transfersRes, storesRes, inventoryRes] = await Promise.all([
                    api.get('/transfers'),
                    api.get('/stores'),
                    api.get(`/inventory/store/${userStoreId}`)
                ]);

                setTransfers(transfersRes.data);
                setAllStores(storesRes.data); // Guardamos la lista completa
                setDestinationStores(storesRes.data.filter(s => s.id !== userStoreId)); // Filtramos para el selector
                setInventory(inventoryRes.data);
            } catch (err) {
                setError('No se pudieron cargar los datos necesarios para las transferencias.');
                console.error("Error en fetchData de Transferencias:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [userStoreId]);

    // --- MANEJADORES ---
    const handleAddItem = () => {
        if (!currentItem.product || currentItem.quantity <= 0 || currentItem.quantity > currentItem.product.quantity) {
            alert('Producto inválido, la cantidad debe ser mayor a 0 y no exceder el stock actual.');
            return;
        }
        setTransferItems([...transferItems, { ...currentItem.product, transfer_quantity: currentItem.quantity }]);
        setCurrentItem({ product: null, quantity: 1 });
    };

    const handleRemoveItem = (productId) => {
        setTransferItems(transferItems.filter(item => item.product_id !== productId));
    };

    const handleCreateTransfer = async () => {
        if (!newTransfer.destination_store_id || transferItems.length === 0) {
            alert('Selecciona una tienda de destino y añade al menos un producto.');
            return;
        }
        try {
            const payload = {
                origin_store_id: userStoreId,
                destination_store_id: newTransfer.destination_store_id,
                items: transferItems.map(item => ({ product_id: item.product_id, quantity: item.transfer_quantity }))
            };
            await api.post('/transfers', payload);
            alert('Transferencia creada exitosamente.');
            window.location.reload();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al crear la transferencia.');
        }
    };

    const handleOpenDetails = (id) => {
        setSelectedTransferId(id);
        setDetailsModalOpen(true);
    };

    const handleReceiveTransfer = async (id) => {
        // Esta función ahora es llamada desde el modal de detalles
        if (window.confirm('¿Confirmas la recepción? Esta acción no se puede deshacer.')) {
            try {
                await api.post(`/transfers/${id}/receive`);
                setDetailsModalOpen(false); // Cerrar el modal
                alert('Transferencia recibida y stock actualizado.');
                window.location.reload(); // Recargar todo
            } catch (err) {
                alert(err.response?.data?.message || 'Error al recibir la transferencia.');
            }
        }
    };


    // --- LÓGICA DE FILTRADO ---
    const currentUserStoreName = allStores.find(s => s.id === userStoreId)?.name;
    const incomingTransfers = transfers.filter(t =>
        t.destination_store_name === currentUserStoreName &&
        t.status === 'Enviado'
    );

    const historyColumns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'origin_store_name', headerName: 'Origen', width: 200 },
        { field: 'destination_store_name', headerName: 'Destino', width: 200 },
        { field: 'status', headerName: 'Estado', width: 120 },
        { field: 'creator_name', headerName: 'Creado por', width: 200 },
        { field: 'created_at', headerName: 'Fecha', type: 'dateTime', valueGetter: (p) => new Date(p.value), width: 180 },
    ];

    // --- RENDERIZADO ---
    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Gestión de Transferencias</Typography>

            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6">Crear Nueva Transferencia</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Enviar a Tienda</InputLabel>
                            <Select
                                value={newTransfer.destination_store_id}
                                onChange={e => setNewTransfer({ destination_store_id: e.target.value })}
                                label="Enviar a Tienda"
                            >
                                {destinationStores.map(store => <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Autocomplete
                            options={inventory}
                            getOptionLabel={(option) => `${option.name} (Stock: ${option.quantity})`}
                            isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                            value={currentItem.product}
                            onChange={(e, newValue) => setCurrentItem({ ...currentItem, product: newValue })}
                            renderInput={(params) => <TextField {...params} label="Buscar Producto en mi Inventario" />}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth label="Cantidad" type="number" value={currentItem.quantity} onChange={e => setCurrentItem({ ...currentItem, quantity: e.target.value })} />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <Button variant="contained" onClick={handleAddItem}>Añadir</Button>
                    </Grid>
                </Grid>
                {transferItems.length > 0 && (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead><TableRow><TableCell>Producto</TableCell><TableCell>Cantidad a Enviar</TableCell><TableCell>Acción</TableCell></TableRow></TableHead>
                            <TableBody>
                                {transferItems.map(item => (
                                    <TableRow key={item.product_id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.transfer_quantity}</TableCell>
                                        <TableCell><IconButton onClick={() => handleRemoveItem(item.product_id)}><DeleteIcon color="error" /></IconButton></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Button variant="contained" color="primary" sx={{ mt: 2, width: '100%' }} onClick={handleCreateTransfer}>Confirmar y Enviar Transferencia</Button>
                    </TableContainer>
                )}
            </Paper>



 <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6">Transferencias Pendientes de Recibir</Typography>
                <TableContainer>
                    <Table>
                        <TableHead><TableRow>...</TableRow></TableHead>
                        <TableBody>
                            {incomingTransfers.length === 0 ? <TableRow>...</TableRow> :
                            incomingTransfers.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell>{t.id}</TableCell>
                                    <TableCell>{t.origin_store_name}</TableCell>
                                    <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        {/* --- BOTÓN CAMBIADO --- */}
                                        <Button variant="contained" color="primary" size="small" onClick={() => handleOpenDetails(t.id)}>
                                            Revisar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        <TransferDetailsModal
                open={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                transferId={selectedTransferId}
                onReceive={handleReceiveTransfer}
            />

            <Typography variant="h6">Historial de Transferencias</Typography>
            <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                    rows={transfers}
                    columns={historyColumns}
                    loading={loading}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                />
            </Box>
        </Box>
    );
};

export default TransfersPage;